import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
} from "@mui/material";
import { milestoneStatusStyleArray } from "../../config";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import moment from "moment";
import { ReactComponent as NewRenovationIcon } from "../../../../assets/svg/NewRenovationIcon.svg";
import { ReactComponent as NewDesignIcon } from "../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as RightArrowIcon } from "../../../../assets/svg/RightArrowIcon.svg";
import ConfirmModel from "../../../../components/ConfirmModel";
import { getApiData } from "../../../../utils/APIHelper";
import { Setting } from "../../../../utils/Setting";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as SmallDhiramIcon } from "../../../../assets/svg/SmallDhiramIcon.svg";
import { renderCurrencyIcon } from "../../../../utils/CurrencyUtils";
import UploadProof from "./UploadProof";
import MobileView from "./MobileView";
import styles from "../../styles";
import Permission from "../../../../components/Permission";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  textTransform: "capitalize",
}));

export default function Milestones({
  details,
  getProjectDetails,
  isMobile,
  styles,
}) {
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]); // Lift state up to parent

  const navigate = useNavigate();
  const { id } = useParams();

  // Check if device is mobile
  const isMobileDevice = isMobile;

  // Get styles with mobile parameter
  const componentStyles = styles || styles(isMobileDevice);

  async function startMilestone(data) {
    try {
      const response = await getApiData(
        Setting.endpoints.startMilestone,
        "PUT",
        {
          proposal_id: details?.proposal_id,
          milestone_id: data?.id,
          status: "ongoing",
        }
      );
      if (response.success) {
        toast.success(response?.message);
        setSelectedMilestone(null);
        getProjectDetails();
      }
    } catch (error) {
      toast.error(error || "Something wrong");
    }
  }

  async function submitMilestone(selectedMilestone) {
    try {
      const response = await getApiData(
        `/api/project/milestone/${selectedMilestone?.id}/delivery`,
        "POST",
        {}
      );
      if (response.success) {
        toast.success(response?.message);
        setSelectedMilestone(null);
        getProjectDetails();
        handleCloseModal();
      }
    } catch (error) {
      console.log("err===>", error);
    }
  }

  const handleConfirm = () => {
    if (selectedMilestone?.status === "approved") {
      startMilestone(selectedMilestone);
    } else {
      submitMilestone(selectedMilestone);
    }
  };

  const handleClick = (item) => {
    console.log("item", item, id);
    navigate(`/project/details/${id}/milestone/${item.id}`, {
      state: {
        milestoneDetails: item,
        project: details,
        styles: componentStyles,
      },
    });
  };

  const handleOpenModal = (item) => {
    setSelectedMilestone(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedMilestone(null);
    setIsModalOpen(false);
    setUploadedImages([]); // Reset uploaded images when modal closes
  };

  if (isMobileDevice) {
    return (
      <MobileView
        details={details}
        getProjectDetails={getProjectDetails}
        handleOpenModal={handleOpenModal}
        selectedMilestone={selectedMilestone}
        isModalOpen={isModalOpen}
        handleCloseModal={handleCloseModal}
        handleConfirm={handleConfirm}
        startMilestone={startMilestone}
        submitMilestone={submitMilestone}
        handleClick={handleClick}
        uploadedImages={uploadedImages}
        setUploadedImages={setUploadedImages}
      />
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          overflow: "visible",
          boxShadow: "none",
          marginTop: "2vh",
        }}
      >
        <Table
          sx={{
            "& thead": {
              "& th": {
                color: "#949DAA",
                fontWeight: "500",
              },
            },
            "& tbody": {
              "& tr:hover": {
                backgroundColor: "#f7f7f7",
              },
              "& tr": {
                borderBottom: "none",
              },
            },
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Milestone</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Start Date</StyledTableCell>
              <StyledTableCell>End Date</StyledTableCell>
              <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                <strong
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  Amount ({renderCurrencyIcon('small')})
                </strong>
              </StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
              <StyledTableCell style={{ visibility: "hidden" }}>
                arrow
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {details?.milestone?.map((item, idx) => (
              <>
                <TableRow key={item.id}>
                  <StyledTableCell>
                    <Box display="flex" alignItems="center">
                      <Box
                        component="img"
                        src={
                          item?.milestone_image
                            ? item?.milestone_image?.path
                            : "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                        }
                        alt="Project Icon"
                        sx={{
                          width: 45,
                          height: 45,
                          borderRadius: "8px",
                          border: "2px solid white",
                          boxShadow: "0px 4px 2.67px 0px #00000026",
                          marginRight: 1,
                        }}
                      />
                      <span>{item?.milestone_name}</span>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap="5px">
                      <Box display="flex" alignItems="center">
                        {item?.tags === "Design" ? (
                          <NewDesignIcon />
                        ) : (
                          <NewRenovationIcon />
                        )}
                      </Box>
                      <Box color="#949DAA">{item?.tags}</Box>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <p style={componentStyles.greyRow}>
                      {moment(item?.start_date).format("MMM DD, YYYY")}
                    </p>
                  </StyledTableCell>
                  <StyledTableCell>
                    <p style={componentStyles.greyRow}>
                      {moment(item?.end_date).format("MMM DD, YYYY")}
                    </p>
                  </StyledTableCell>
                  <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                    {`${formattedAmountWithoutAed(item?.amount)}`}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <Box
                        sx={{
                          width: "15px",
                          height: "15px",
                          borderRadius: "50%",
                          border: "3px solid rgb(229, 229, 229)",
                          backgroundColor:
                            milestoneStatusStyleArray.find(
                              (style) => style.status === item?.status
                            )?.backgroundColor || "#FFAD5C",
                        }}
                      />
                      <Box
                        sx={{
                          fontSize: "14px",
                          color: "#0D0D0E",
                        }}
                      >
                        {getStatusDisplayText(item?.status)}
                      </Box>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Permission resource="MILESTONES" action="UPDATE">
                      {item?.status === "approved" ||
                        item?.status === "ongoing" ? (
                        <button
                          onClick={() => handleOpenModal(item)}
                          style={componentStyles.milestoneBtn}
                        >
                          {item?.status === "approved"
                            ? "Start Milestone"
                            : "Submit Milestone"}
                        </button>
                      ) : (
                        "-"
                      )}
                    </Permission>
                  </StyledTableCell>
                  <StyledTableCell>
                    <RightArrowIcon
                      style={{ cursor: "pointer" }}
                      onClick={() => handleClick(item)}
                    />
                  </StyledTableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedMilestone &&
        (selectedMilestone?.status === "approved" ? (
          <ConfirmModel
            visible={isModalOpen}
            handleClose={handleCloseModal}
            confirmation={handleConfirm}
            message={`Are you sure you want to start ${selectedMilestone?.milestone_name}?`}
          />
        ) : (
          <UploadProof
            open={isModalOpen}
            handleClose={handleCloseModal}
            selectedMilestone={selectedMilestone}
            handleConfirm={handleConfirm}
            uploadedImages={uploadedImages}
            setUploadedImages={setUploadedImages}
          />
        ))}
    </>
  );
}
